"use client";

import { useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ChevronLeft, Info, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import useFeedbackModal from "../hooks/use-feedback-modal";

interface BugReportFormProps {
  onBack: () => void;
  onSuccess: () => void;
}

const SEVERITIES = [
  {
    value: "Minor" as const,
    labelKey: "feedbackModal.bugReport.severityMinor" as const,
    color: "text-slate-500",
    borderColor: "border-slate-400",
    bg: "bg-slate-50",
  },
  {
    value: "Annoying" as const,
    labelKey: "feedbackModal.bugReport.severityAnnoying" as const,
    color: "text-amber-500",
    borderColor: "border-amber-400",
    bg: "bg-amber-50",
  },
  {
    value: "Unusable" as const,
    labelKey: "feedbackModal.bugReport.severityUnusable" as const,
    color: "text-red-600",
    borderColor: "border-red-500",
    bg: "bg-red-50",
  },
] as const;

export const BugReportForm = ({ onBack, onSuccess }: BugReportFormProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<"Minor" | "Annoying" | "Unusable">(
    "Annoying",
  );
  // const [screenshot, setScreenshot] = useState<string | null>(null);
  // const [screenshotName, setScreenshotName] = useState<string | null>(null);
  // const fileInputRef = useRef<HTMLInputElement>(null);
  const { canSubmitBug, recordBugSubmission } = useFeedbackModal();
  const t = useI18n();

  const trpc = useTRPC();
  const submitBug = useMutation(
    trpc.feedback.submitBugReport.mutationOptions({
      onSuccess: () => {
        recordBugSubmission();
        onSuccess();
      },
      onError: (error) => {
        toast.error(error.message || t("feedbackModal.bugReport.submitError"));
      },
    }),
  );

  // const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;
  //   if (file.size > 5 * 1024 * 1024) {
  //     toast.error("Screenshot must be under 5MB");
  //     return;
  //   }
  //   const reader = new FileReader();
  //   reader.onload = () => {
  //     setScreenshot(reader.result as string);
  //     setScreenshotName(file.name);
  //   };
  //   reader.readAsDataURL(file);
  // };

  const handleSubmit = () => {
    if (!canSubmitBug()) {
      toast.error(t("feedbackModal.bugReport.rateLimitError"));
      return;
    }
    const metadata = {
      userAgent: navigator.userAgent,
      pageUrl: window.location.href,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
    };

    submitBug.mutate({
      title: title.trim(),
      description: description.trim() || undefined,
      severity,
      // screenshotBase64: screenshot || undefined,
      metadata,
    });
  };

  const canSubmit = title.trim().length > 0 && !submitBug.isPending;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.15 }}
      className="flex flex-col gap-4"
    >
      {/* Title */}
      <div>
        <label
          htmlFor="bug-report-title"
          className="mb-1.5 block text-[13px] font-medium text-foreground"
        >
          {t("feedbackModal.bugReport.whatHappened")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <Input
          id="bug-report-title"
          placeholder={t("feedbackModal.bugReport.whatHappenedPlaceholder")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="focus-visible:ring-[#0077FF]"
        />
      </div>

      {/* Steps to reproduce */}
      <div>
        <label
          htmlFor="bug-report-description"
          className="mb-1.5 block text-[13px] font-medium text-foreground"
        >
          {t("feedbackModal.bugReport.stepsToReproduce")}
        </label>
        <Textarea
          id="bug-report-description"
          placeholder={t("feedbackModal.bugReport.stepsToReproducePlaceholder")}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="resize-vertical focus-visible:ring-[#0077FF]"
        />
      </div>

      {/* Severity */}
      <div>
        <span className="mb-2 block text-[13px] font-medium text-foreground">
          {t("feedbackModal.bugReport.severityLabel")}
        </span>
        <div className="flex gap-2">
          {SEVERITIES.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSeverity(s.value)}
              className={cn(
                "flex-1 rounded-lg border-[1.5px] px-3 py-2 text-[13px] font-medium transition-all cursor-pointer",
                severity === s.value
                  ? cn(s.borderColor, s.bg, s.color)
                  : "border-border text-muted-foreground hover:border-border/80",
              )}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Screenshot upload — commented out
      <div>
        <label className="mb-1.5 block text-[13px] font-medium text-foreground">
          Screenshot{" "}
          <span className="text-xs font-normal text-muted-foreground">
            (optional)
          </span>
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        {screenshot ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
            <span className="flex-1 truncate text-[13px] text-muted-foreground">
              {screenshotName}
            </span>
            <button
              type="button"
              onClick={() => {
                setScreenshot(null);
                setScreenshotName(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
              className="text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="size-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed border-border px-4 py-4 text-[13px] text-muted-foreground transition-colors hover:border-border/80 hover:text-foreground"
          >
            <Upload className="size-4" />
            Drop an image or click to upload
          </button>
        )}
      </div>
      */}

      {/* Auto-metadata notice */}
      <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground">
        <Info className="size-3.5 shrink-0" />
        {t("feedbackModal.bugReport.autoMetadata")}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-1">
          <ChevronLeft className="size-4" />
          {t("feedbackModal.back")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="bg-red-600 hover:bg-red-700 disabled:bg-muted disabled:text-muted-foreground"
        >
          {submitBug.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            t("feedbackModal.bugReport.submitButton")
          )}
        </Button>
      </div>
    </motion.div>
  );
};
